'use client';

import { Check, Minus } from 'lucide-react';
import { Checkbox as RadixCheckbox } from 'radix-ui';
import { type ReactNode, useId } from 'react';
import { cn, focusRing } from './cn';

/** A choice saved with the form's button. For a setting applied at once, use Switch. */
export function Checkbox({
  checked,
  defaultChecked,
  onCheckedChange,
  indeterminate = false,
  label,
  hint,
  disabled,
  name,
  className,
  id: idProp,
  'aria-label': ariaLabel,
}: {
  checked?: boolean;
  /** Uncontrolled: the box keeps its own state, read from the form by `name`. */
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Some of what the box stands for is on: shows a dash, and a click checks it. */
  indeterminate?: boolean;
  label?: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  name?: string;
  className?: string;
  /** Id of the box, for a label or a test that points at it. */
  id?: string;
  /** Accessible name when there is no visible label. */
  'aria-label'?: string;
}) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const box = (
    <RadixCheckbox.Root
      id={id}
      name={name}
      checked={indeterminate ? 'indeterminate' : checked}
      defaultChecked={defaultChecked}
      onCheckedChange={(value) => onCheckedChange?.(value === true)}
      aria-label={ariaLabel}
      aria-describedby={label && hint ? `${id}-hint` : undefined}
      disabled={disabled}
      className={cn(
        'grid size-4 shrink-0 place-items-center rounded-[4px] border border-crow-input bg-crow-surface transition-colors duration-[var(--crow-dur-1)] disabled:opacity-50 data-[state=checked]:border-crow-accent data-[state=checked]:bg-crow-accent data-[state=indeterminate]:border-crow-accent data-[state=indeterminate]:bg-crow-accent',
        focusRing,
        label ? 'mt-0.5' : className,
      )}
    >
      <RadixCheckbox.Indicator>
        {indeterminate ? (
          <Minus className="size-3 text-crow-on-accent" strokeWidth={3} />
        ) : (
          <Check className="size-3 text-crow-on-accent" strokeWidth={3} />
        )}
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );
  if (!label) return box;
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      {box}
      {/* The hint stays out of the label, so the box is named by the label alone. */}
      <div className="min-w-0">
        <label htmlFor={id} className="block text-crow-body text-crow-text">
          {label}
        </label>
        {hint ? (
          <p id={`${id}-hint`} className="mt-0.5 text-crow-label text-crow-text-3">
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}
